import { Form } from '@unform/web';
import * as Yup from 'yup';
import {
  Button,
  Container,
  Divider,
  FormControl,
  FormLabel,
  Grid,
  IconButton,
  makeStyles,
  Theme,
  Typography,
} from '@material-ui/core';
import { Create, Warning, Visibility, VisibilityOff } from '@material-ui/icons';
import { yellow } from '@material-ui/core/colors';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from '@mui/material';

import React, { useCallback, useEffect, useRef, useState } from 'react';

import { FormHandles } from '@unform/core';
import Loader from '../../components/Loader';
import Snackbar, { AlertComponentProps } from '../../components/Snackbar';
import { useAxios } from '../../utils/hooks';
import { Signature as SignatureModel } from '../../models/signature';
import Input from '../../components/Input';
import getValidationErrors from '../../utils/getValidationErrors';
import Select from '../../components/Select';

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    marginTop: theme.spacing(1),
    padding: theme.spacing(1),
    background: theme.palette.background.paper,
  },
  title: {
    color: theme.palette.secondary.light,
  },
  input: {
    display: 'none',
  },
  divider: {
    margin: theme.spacing(1),
    width: '100%',
  },
  img: {
    width: '100%',
    background: 'transparent',
  },
  warning: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '1.1rem',
  },
  formTitles: {
    fontWeight: 'bolder',
  },
  password: {
    display: 'flex',
    justifyContent: 'center',
    padding: theme.spacing(2),
  },
  typogra: {
    margin: theme.spacing(2),
  },
  formControl: {
    margin: theme.spacing(2, 0),
  },
}));

const boards = [
  { value: 'CRF', label: 'CRF' },
  { value: 'CRN2', label: 'CRN2' },
  { value: 'CRP', label: 'CRP' },
  { value: 'ABPp', label: 'ABPp' },
  { value: 'CRM', label: 'CRM' },
  { value: 'CRESS', label: 'CRESS' },
  // { value: 'UBAM', label: 'UBAM' }, // musicoterapia
  // { value: 'CFP', label: 'CFP' },
  // { value: 'CRP', label: 'CRP' },
  // { value: 'CFM', label: 'CFM' },
  // { value: 'CRM', label: 'CRM' },
  { value: 'CPF', label: 'CPF' },
];

const Signature: React.FC = () => {
  const api = useAxios();
  const classes = useStyles();
  const formRef = useRef<FormHandles>(null);
  const form2Ref = useRef<FormHandles>(null);
  const [loading, setLoading] = useState(true);
  const [showPass, setShowPass] = useState(false);
  const [openCreatePass, setOpenCreatePass] = useState(false);
  const [openUpdatePass, setOpenUpdatePass] = useState(false);
  const [hasSignature, setHasSignature] = useState<boolean | null>(null);
  const [hasSignaturePassword, setHasSignaturePassword] =
    useState<boolean | null>(null);
  const [signature, setSignature] = useState<SignatureModel | null>(null);

  const [alert, setAlert] = useState<
    AlertComponentProps & { isActive: boolean }
  >({
    isActive: false,
    type: 'info',
    message: 'none',
  });

  useEffect(() => {
    async function userHasSignature() {
      try {
        const hasSignatureResponse = await api.current?.get(
          `/users/hasSignature`,
        );

        setHasSignature(hasSignatureResponse?.data.hasSignature);

        if (hasSignatureResponse?.data.hasSignature === true) {
          const signatureResponse = await api.current?.get(`/users/signature`);
          setSignature(signatureResponse?.data);
        }

        const hasSignaturePasswordResponse = await api.current?.get(
          `/users/hasSignaturePassword`,
        );

        setHasSignaturePassword(
          hasSignaturePasswordResponse?.data.hasSignaturePassword,
        );
      } catch (error) {
        setAlert({
          isActive: true,
          type: 'error',
          message: 'Erro ao buscar assinatura.',
        });
      } finally {
        setLoading(false);
      }
    }

    userHasSignature();
  }, [api]);

  const handleSignature = async ({ target }: any) => {
    setLoading(true);
    const file = target.files[0];

    try {
      const formData = new FormData();
      formData.append('file', file);
      const fileResponse = await api.current?.post(
        '/files/signature',
        formData,
      );

      if (fileResponse?.data) {
        const signatureResponse = await api.current?.patch('/users/signature', {
          ...signature,
          file_id: fileResponse.data._id,
        });

        setSignature(signatureResponse?.data);
        setHasSignature(true);

        setAlert({
          isActive: true,
          type: 'success',
          message: 'Assinatura cadastrada com sucesso.',
        });
      }
    } catch (error) {
      setAlert({
        isActive: true,
        type: 'error',
        message: 'Erro ao cadastrar assinatura.',
      });
    } finally {
      setLoading(false);
    }
  };

  const toggleShowPass = () => {
    setShowPass(!showPass);
  };

  const handleOpenCreatePass = () => {
    setOpenCreatePass(true);
  };

  const handleCloseCreatePass = () => {
    setOpenCreatePass(false);
  };

  const handleOpenUpdatePass = () => {
    setOpenUpdatePass(true);
  };

  const handleCloseUpdatePass = () => {
    setOpenUpdatePass(false);
  };

  const getPassword = (password: number) => {
    const stringPassword = password.toString();

    const numLength = stringPassword.replace(/^-|\./g, '').length;

    if (numLength === 3) {
      return `0${stringPassword}`;
    }

    if (numLength === 2) {
      return `00${stringPassword}`;
    }

    if (numLength === 1) {
      return `000${stringPassword}`;
    }

    return stringPassword;
  };

  const resetAlert = () => {
    setAlert({
      isActive: false,
      type: 'info',
      message: 'none',
    });
  };

  const handleProfessionalData = useCallback(
    async (data) => {
      try {
        resetAlert();
        form2Ref.current?.setErrors({});

        const schema = Yup.object().shape({
          name: Yup.string().required('Nome é obrigatório'),
          number: Yup.string().required('Número de registro é obrigatório'),
          register: Yup.string().required('Conselho de registro é obrigatório'),
        });

        await schema.validate(data, { abortEarly: false });

        await api.current?.patch(`/users/signature`, {
          file_id: signature && signature.file ? signature?.file._id : null,
          ...data,
        });

        setAlert({
          isActive: true,
          type: 'success',
          message: 'Assinatura atualizada com sucesso.',
        });

        const currentSignature = signature || ({} as SignatureModel);
        currentSignature.name = data.name;
        currentSignature.number = data.number;
        currentSignature.register = data.register;
        setSignature(currentSignature);
      } catch (error) {
        if (error instanceof Yup.ValidationError) {
          const errors = getValidationErrors(error);

          form2Ref.current?.setErrors(errors);
        } else {
          setAlert({
            isActive: true,
            type: 'error',
            message: 'Erro ao atualizar dados da assinatura.',
          });
        }
      }
    },
    [api, signature],
  );

  const handleSubmitCreatePass = useCallback(
    async (data: { password: number }) => {
      try {
        resetAlert();
        formRef.current?.setErrors({});

        const schema = Yup.object().shape({
          password: Yup.string()
            .matches(/^[0-9]+$/, 'Somente números permitidos')
            .min(4, 'Senha deve conter 4 dígitos')
            .max(4, 'Senha deve conter 4 dígitos')
            .required('Senha é obrigatória'),
        });

        await schema.validate(
          { password: data.password },
          { abortEarly: false },
        );

        data.password = Number(data.password);

        await api.current?.post(`/users/signature/password`, data);

        setAlert({
          isActive: true,
          type: 'success',
          message: 'Senha criada com sucesso.',
        });

        setHasSignaturePassword(true);

        if (signature) {
          const currentSignature = signature;
          currentSignature.password = data.password;
          setSignature(currentSignature);
        }

        handleCloseCreatePass();
      } catch (error) {
        if (error instanceof Yup.ValidationError) {
          const errors = getValidationErrors(error);

          formRef.current?.setErrors(errors);
        } else {
          setAlert({
            isActive: true,
            type: 'error',
            message: 'Erro ao criar senha.',
          });
        }
      }
    },
    [api, signature],
  );

  const handleSubmitUpdatePass = useCallback(
    async (data: { new_password: number; old_password: number }) => {
      try {
        resetAlert();
        formRef.current?.setErrors({});

        const schema = Yup.object().shape({
          new_password: Yup.string()
            .matches(/^[0-9]+$/, 'Somente números permitidos')
            .min(4, 'Senha deve conter 4 dígitos')
            .max(4, 'Senha deve conter 4 dígitos')
            .required('Senha é obrigatória'),
          old_password: Yup.string()
            .matches(/^[0-9]+$/, 'Somente números permitidos')
            .min(4, 'Senha deve conter 4 dígitos')
            .max(4, 'Senha deve conter 4 dígitos')
            .required('Senha é obrigatória'),
        });

        await schema.validate(
          {
            new_password: data.new_password,
            old_password: data.old_password,
          },
          { abortEarly: false },
        );

        data.new_password = Number(data.new_password);
        data.old_password = Number(data.old_password);

        await api.current?.patch(`/users/signature/password`, data);

        setAlert({
          isActive: true,
          type: 'success',
          message: 'Senha atualizada com sucesso.',
        });

        if (signature) {
          const currentSignature = signature;
          currentSignature.password = data.new_password;
          setSignature(currentSignature);
        }

        handleCloseUpdatePass();
      } catch (error) {
        if (error instanceof Yup.ValidationError) {
          const errors = getValidationErrors(error);

          formRef.current?.setErrors(errors);
        } else {
          setAlert({
            isActive: true,
            type: 'error',
            message: 'Erro ao atualizar senha.',
          });
        }
      }
    },
    [api, signature],
  );

  return (
    <Container>
      <Loader loading={loading} />

      <Dialog
        open={openCreatePass}
        onClose={handleCloseCreatePass}
        aria-labelledby="form-dialog-title"
      >
        <Form ref={formRef} onSubmit={handleSubmitCreatePass}>
          <DialogTitle id="form-dialog-title">Cadastrar Senha</DialogTitle>

          <DialogContent>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12}>
                <FormLabel className={classes.formTitles}>
                  Insira a senha para assinar digitalmente
                </FormLabel>
                <br />
                <span>
                  (deve existir uma assinatura previamente cadastrada)
                </span>
              </Grid>

              <Grid item xs={12}>
                <Input
                  id="password"
                  label="Senha"
                  name="password"
                  size={4}
                  maxLength={4}
                />
              </Grid>
            </Grid>
          </DialogContent>

          <DialogActions>
            <Button onClick={handleCloseCreatePass}>Cancelar</Button>
            <Button type="submit" color="primary" variant="contained">
              Salvar
            </Button>
          </DialogActions>
        </Form>
      </Dialog>

      <Dialog
        open={openUpdatePass}
        onClose={handleCloseUpdatePass}
        aria-labelledby="form-dialog-title"
      >
        <Form ref={formRef} onSubmit={handleSubmitUpdatePass}>
          <DialogTitle id="form-dialog-title">Atualizar Senha</DialogTitle>

          <DialogContent>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12}>
                <FormLabel className={classes.formTitles}>
                  Insira os dados para atualizar sua senha
                </FormLabel>
              </Grid>

              <Grid item xs={12}>
                <Input
                  id="old_password"
                  label="Senha atual"
                  name="old_password"
                  size={4}
                  maxLength={4}
                />
              </Grid>

              <Grid item xs={12}>
                <Input
                  id="new_password"
                  label="Nova senha"
                  name="new_password"
                  size={4}
                  maxLength={4}
                />
              </Grid>
            </Grid>
          </DialogContent>

          <DialogActions>
            <Button onClick={handleCloseUpdatePass}>Cancelar</Button>
            <Button type="submit" color="primary" variant="contained">
              Salvar
            </Button>
          </DialogActions>
        </Form>
      </Dialog>

      <Grid
        container
        spacing={2}
        className={classes.root}
        style={{ display: loading ? 'none' : '' }}
      >
        <Grid item xs={12}>
          <h2 className={classes.title}>Assinatura Digital</h2>
        </Grid>

        <Divider className={classes.divider} />

        <Grid
          container
          spacing={2}
          className={classes.root}
          style={{ display: loading ? 'none' : '' }}
        >
          <Grid item xs={5}>
            {!signature?.file && (
              <Grid item xs={12}>
                <Typography
                  variant="h6"
                  component="h1"
                  color="textSecondary"
                  className={classes.warning}
                >
                  <Warning style={{ color: yellow[800] }} />
                  Usuário não possui assinatura cadastrada.
                </Typography>
              </Grid>
            )}

            {signature && signature.file && (
              <Grid item xs={12}>
                <Typography
                  align="center"
                  variant="h6"
                  component="h1"
                  color="textSecondary"
                >
                  Assinatura atual
                </Typography>

                <Typography align="center">
                  <img
                    alt="userSignature"
                    className={classes.img}
                    src={`${process.env.REACT_APP_PRONTUARIO_API}/${signature.file.path}`}
                  />
                </Typography>
              </Grid>
            )}

            <Grid item xs={12}>
              <Typography align="center" className={classes.typogra}>
                <input
                  className={classes.input}
                  id="faceImage"
                  type="file"
                  onChange={handleSignature}
                />
                <FormLabel htmlFor="faceImage">
                  <Button
                    startIcon={<Create />}
                    color="primary"
                    component="span"
                    variant="contained"
                    aria-label="upload signature"
                  >
                    {`${hasSignature ? 'Atualizar' : 'Cadastrar'} Assinatura`}
                  </Button>
                </FormLabel>
              </Typography>
            </Grid>
          </Grid>

          <Grid
            item
            xs={2}
            style={{ display: 'flex', justifyContent: 'center' }}
          >
            <Divider orientation="vertical" />
          </Grid>

          <Grid item xs={5}>
            {hasSignaturePassword === false && (
              <Grid item xs={12}>
                <Typography
                  align="center"
                  variant="h6"
                  component="h1"
                  color="textSecondary"
                  className={classes.warning}
                >
                  <Warning style={{ color: yellow[800] }} />
                  Usuário não possui uma senha cadastrada.
                </Typography>
              </Grid>
            )}

            {hasSignaturePassword === true && signature && signature?.password && (
              <Grid item xs={12}>
                <Typography
                  align="center"
                  variant="h6"
                  component="h1"
                  color="textSecondary"
                >
                  Senha atual
                </Typography>

                <div className={classes.password}>
                  <Typography component="h1" variant="h3">
                    {showPass ? getPassword(signature?.password) : '****'}
                  </Typography>

                  <IconButton
                    color="primary"
                    aria-label="show password"
                    component="span"
                    onClick={() => toggleShowPass()}
                  >
                    {showPass ? (
                      <VisibilityOff color="primary" />
                    ) : (
                      <Visibility color="primary" />
                    )}
                  </IconButton>
                </div>
              </Grid>
            )}

            <Grid item xs={12}>
              <Typography align="center" className={classes.typogra}>
                <Button
                  startIcon={<Create />}
                  color="primary"
                  component="span"
                  variant="contained"
                  aria-label="upload signature"
                  onClick={() => {
                    return hasSignaturePassword
                      ? handleOpenUpdatePass()
                      : handleOpenCreatePass();
                  }}
                >
                  {`${hasSignaturePassword ? 'Atualizar' : 'Cadastrar'} Senha`}
                </Button>
              </Typography>
            </Grid>
          </Grid>

          <Grid item xs={12}>
            <Divider />
          </Grid>

          <Grid item xs={12}>
            {(!signature ||
              !signature?.name ||
              !signature?.number ||
              !signature?.register) && (
              <Grid item xs={12}>
                <Typography
                  align="center"
                  variant="h6"
                  component="h1"
                  color="textSecondary"
                  className={classes.warning}
                >
                  <Warning style={{ color: yellow[800] }} />
                  Dados profissionais faltando para assinatura.
                </Typography>
              </Grid>
            )}

            {!loading && (
              <Grid item xs={12}>
                <Form
                  id="form2"
                  ref={form2Ref}
                  initialData={signature || {}}
                  onSubmit={handleProfessionalData}
                  className={classes.root}
                >
                  <FormControl fullWidth className={classes.formControl}>
                    <Grid container spacing={2} alignItems="center">
                      <Grid item xs={4}>
                        <Input id="name" label="Nome" name="name" />
                      </Grid>

                      <Grid item xs={4}>
                        <Input
                          id="number"
                          label="Número de Registro"
                          name="number"
                        />
                      </Grid>

                      <Grid item xs={4}>
                        <Select
                          name="register"
                          isMulti={false}
                          placeholder="Conselho"
                          menuPosition="fixed"
                          options={boards}
                          defaultValue={
                            signature?.register
                              ? {
                                  value: signature.register,
                                  label: signature.register,
                                }
                              : null
                          }
                        />
                      </Grid>
                    </Grid>
                  </FormControl>

                  <Grid item xs={12}>
                    <Typography align="center" className={classes.typogra}>
                      <Button
                        startIcon={<Create />}
                        type="submit"
                        color="primary"
                        variant="contained"
                      >
                        Atualizar dados
                      </Button>
                    </Typography>
                  </Grid>
                </Form>
              </Grid>
            )}
          </Grid>
        </Grid>
      </Grid>

      {alert.isActive && <Snackbar {...alert} />}
    </Container>
  );
};

export default Signature;
